import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { API, axios } from "@/api";
import {
  Sparkles, Navigation, Calendar, Clock, Star, Gift,
  Loader2, Coffee, UtensilsCrossed, IceCream2, ShoppingBag,
  Palette, Clapperboard, Dumbbell, Filter, ChevronRight,
  Target, Zap, MapPin, AlertTriangle, Smartphone,
  CheckCircle2, ShieldCheck, DollarSign, Timer, ArrowUpDown,
} from "lucide-react";

const CATEGORY_OPTIONS = [
  { id: "Coffee & Tea", label: "Coffee", icon: Coffee, color: "bg-amber-50 text-amber-700 border-amber-300", dbCats: ["Coffee", "Coffee/Cafe", "Boba/Tea"] },
  { id: "Fast Food", label: "Fast Food", icon: Zap, color: "bg-red-50 text-red-700 border-red-300", dbCats: ["Fast Food", "Fast Casual"] },
  { id: "Casual Dining", label: "Dining", icon: UtensilsCrossed, color: "bg-orange-50 text-orange-700 border-orange-300", dbCats: ["Restaurant", "Restaurant/Buffet", "Restaurant/Wine Bar"] },
  { id: "Ice Cream & Dessert", label: "Dessert", icon: IceCream2, color: "bg-pink-50 text-pink-700 border-pink-300", dbCats: ["Dessert", "Dessert/Ice Cream", "Ice Cream / Dessert", "Snack"] },
  { id: "Retail", label: "Retail", icon: ShoppingBag, color: "bg-violet-50 text-violet-700 border-violet-300", dbCats: ["Retail", "Grocery", "Market/Specialty", "Convenience"] },
  { id: "Beauty", label: "Beauty", icon: Palette, color: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-300", dbCats: ["Cosmetics", "Entertainment/Spa"] },
  { id: "Entertainment", label: "Fun", icon: Clapperboard, color: "bg-sky-50 text-sky-700 border-sky-300", dbCats: ["Entertainment"] },
  { id: "Fitness", label: "Fitness", icon: Dumbbell, color: "bg-emerald-50 text-emerald-700 border-emerald-300", dbCats: ["Service"] },
  { id: "Bakery / Dessert", label: "Bakery", icon: Gift, color: "bg-rose-50 text-rose-700 border-rose-300", dbCats: ["Bakery", "Bakery/Cafe"] },
  { id: "Restaurant", label: "Restaurant", icon: UtensilsCrossed, color: "bg-orange-50 text-orange-700 border-orange-300", dbCats: ["Restaurant", "Restaurant/Buffet", "Restaurant/Wine Bar"] },
];

const expandCategories = (selectedIds) => {
  const dbCats = new Set();
  selectedIds.forEach(id => {
    const opt = CATEGORY_OPTIONS.find(c => c.id === id);
    if (opt) opt.dbCats.forEach(dc => dbCats.add(dc));
    else dbCats.add(id);
  });
  return [...dbCats];
};

const TRIP_TYPES = [
  { id: "half_day", label: "Half Day", desc: "~4 stops", icon: "☀️" },
  { id: "full_day", label: "Full Day", desc: "~8 stops", icon: "🌈" },
  { id: "multi_day", label: "Multi-Day", desc: "3+ days", icon: "🎂" },
];

const PRIORITY_OPTIONS = [
  { id: "value", label: "Highest Value", desc: "Maximize your savings", icon: DollarSign, color: "text-emerald-600" },
  { id: "ease", label: "Easiest Claims", desc: "Quick & simple stops", icon: CheckCircle2, color: "text-sky-600" },
  { id: "distance", label: "Closest First", desc: "Shortest drive time", icon: MapPin, color: "text-pink-600" },
  { id: "rating", label: "Best Rated", desc: "Top confidence scores", icon: Star, color: "text-amber-600" },
];

const EST_DURATION = {
  "Coffee & Tea": 10, "Bakery / Dessert": 10, "Fast Food": 15,
  "Casual Dining": 30, "Restaurant": 30, "Ice Cream & Dessert": 12,
  "Retail": 20, "Clothing": 20, "Beauty": 25, "Entertainment": 45, "Fitness": 30,
};

export default function ItineraryPlanner({ user, onGenerate }) {
  const [step, setStep] = useState(1);
  const [city, setCity] = useState(user?.city || "");
  const [state, setState] = useState(user?.state || "");
  const [detectingGPS, setDetectingGPS] = useState(false);
  const [tripType, setTripType] = useState("full_day");
  const [numDays, setNumDays] = useState(3);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priorities, setPriorities] = useState(["value", "ease", "distance", "rating"]);
  const [savingsGoal, setSavingsGoal] = useState(50);
  const [timeWindows, setTimeWindows] = useState({ morning: true, lunch: true, afternoon: true, evening: false });
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const radiusMi = user?.search_radius || 10;
  const totalSteps = 5;

  const detectGPS = async () => {
    setDetectingGPS(true);
    try {
      const pos = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 8000 }));
      const resp = await axios.get(`${API}/geocode/reverse`, { params: { lat: pos.coords.latitude, lng: pos.coords.longitude } });
      if (resp.data?.city) { setCity(resp.data.city); setState(resp.data.state || ""); }
    } catch {}
    finally { setDetectingGPS(false); }
  };

  const movePriority = (idx, dir) => {
    const next = idx + dir;
    if (next < 0 || next >= priorities.length) return;
    const updated = [...priorities];
    [updated[idx], updated[next]] = [updated[next], updated[idx]];
    setPriorities(updated);
  };

  // Preview available offers count
  useEffect(() => {
    if (step !== 3 || selectedCategories.length === 0) return;
    setLoadingPreview(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/itinerary/preview-count`, { params: { categories: expandCategories(selectedCategories).join(","), city, state } });
        setPreview(res.data);
      } catch { setPreview(null); }
      finally { setLoadingPreview(false); }
    }, 500);
    return () => clearTimeout(timer);
  }, [step, selectedCategories, city, state]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/itinerary/generate`, {
        categories: expandCategories(selectedCategories),
        trip_type: tripType,
        num_days: tripType === "multi_day" ? numDays : 1,
        city, state,
        priorities,
        savings_goal: savingsGoal,
        time_windows: timeWindows,
      });
      onGenerate(res.data);
    } catch { /* toast handled in parent */ }
    finally { setGenerating(false); }
  };

  const canProceed = () => {
    if (step === 1) return city.length > 0;
    if (step === 2) return true;
    if (step === 3) return selectedCategories.length > 0;
    return true;
  };

  return (
    <div className="px-4 pt-4 pb-8" data-testid="itinerary-planner">
      {/* Progress */}
      <div className="flex items-center gap-1.5 mb-6">
        {Array.from({ length: totalSteps }, (_, i) => (
          <div key={i} className={`flex-1 h-1.5 rounded-full transition ${i + 1 <= step ? "bg-pink-500" : "bg-slate-200"}`} />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* ═══ STEP 1: Location ═══ */}
        {step === 1 && (
          <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5" data-testid="planner-step-1">
            <div>
              <h2 className="text-xl font-black text-slate-800">Where's the party?</h2>
              <p className="text-sm text-slate-500 mt-1">We'll find birthday deals within <span className="font-bold text-pink-600">{radiusMi} miles</span> of your city.</p>
            </div>
            <div className="space-y-3">
              <div className="flex gap-2">
                <input value={city} onChange={e => setCity(e.target.value)} placeholder="City" className="flex-1 px-4 py-3 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100" data-testid="planner-city" />
                <input value={state} onChange={e => setState(e.target.value)} placeholder="State" className="w-28 px-4 py-3 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100" data-testid="planner-state" />
              </div>
              <button onClick={detectGPS} disabled={detectingGPS} className="w-full py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600 font-medium flex items-center justify-center gap-2 hover:bg-slate-100" data-testid="planner-gps-btn">
                {detectingGPS ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4 text-sky-500" />} Use my current location
              </button>
            </div>
            <div className="bg-sky-50 border border-sky-200 rounded-xl px-4 py-3 flex items-start gap-3" data-testid="radius-info">
              <Target className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-sky-700">Search radius: {radiusMi} miles</p>
                <p className="text-[10px] text-sky-500 mt-0.5">Change this in your profile settings. All stops will be within this distance.</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* ═══ STEP 2: Trip Type ═══ */}
        {step === 2 && (
          <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5" data-testid="planner-step-2">
            <div>
              <h2 className="text-xl font-black text-slate-800">How long's the celebration?</h2>
              <p className="text-sm text-slate-500 mt-1">Plan a quick run, a full day, or an epic birthday week.</p>
            </div>
            <div className="space-y-2">
              {TRIP_TYPES.map(t => (
                <button key={t.id} onClick={() => setTripType(t.id)} className={`w-full p-4 rounded-xl border-2 text-left transition ${tripType === t.id ? "border-pink-400 bg-pink-50 ring-2 ring-pink-200" : "border-slate-200 bg-white hover:border-slate-300"}`} data-testid={`planner-trip-${t.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{t.icon}</span>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-slate-800">{t.label}</p>
                      <p className="text-[10px] text-slate-400">{t.desc}</p>
                    </div>
                    {tripType === t.id && <CheckCircle2 className="w-5 h-5 text-pink-500" />}
                  </div>
                </button>
              ))}
            </div>
            {tripType === "multi_day" && (
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-slate-700">Number of days</p>
                  <span className="text-lg font-black text-pink-600">{numDays}</span>
                </div>
                <input type="range" min={2} max={10} value={numDays} onChange={e => setNumDays(parseInt(e.target.value))} className="w-full accent-pink-500" data-testid="planner-num-days" />
                <div className="flex justify-between text-[9px] text-slate-400 mt-1"><span>2 days</span><span>Birthday week (7)</span><span>10 days</span></div>
              </div>
            )}
            {/* Time Windows */}
            <div>
              <p className="text-xs font-bold text-slate-700 mb-2">When are you available?</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "morning", label: "Morning", time: "8 AM - 11 AM", icon: "🌅" },
                  { id: "lunch", label: "Lunch", time: "11 AM - 2 PM", icon: "🌞" },
                  { id: "afternoon", label: "Afternoon", time: "2 PM - 5 PM", icon: "🌤️" },
                  { id: "evening", label: "Evening", time: "5 PM - 9 PM", icon: "🌙" },
                ].map(w => (
                  <button key={w.id} onClick={() => setTimeWindows(prev => ({ ...prev, [w.id]: !prev[w.id] }))} className={`p-3 rounded-xl border-2 text-left transition ${timeWindows[w.id] ? "border-pink-400 bg-pink-50" : "border-slate-200 bg-white"}`} data-testid={`planner-window-${w.id}`}>
                    <span className="text-lg">{w.icon}</span>
                    <p className="text-xs font-bold text-slate-700 mt-1">{w.label}</p>
                    <p className="text-[9px] text-slate-400">{w.time}</p>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* ═══ STEP 3: Categories ═══ */}
        {step === 3 && (
          <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5" data-testid="planner-step-3">
            <div>
              <h2 className="text-xl font-black text-slate-800">What sounds good?</h2>
              <p className="text-sm text-slate-500 mt-1">Pick the categories you want in your birthday plan.</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {CATEGORY_OPTIONS.map(cat => {
                const sel = selectedCategories.includes(cat.id);
                const Icon = cat.icon;
                return (
                  <button key={cat.id} onClick={() => setSelectedCategories(prev => sel ? prev.filter(c => c !== cat.id) : [...prev, cat.id])} className={`p-3 rounded-xl border-2 text-left flex items-center gap-3 transition ${sel ? cat.color.replace("bg-", "border-").split(" ")[0].replace("50", "400") + " " + cat.color : "border-slate-200 bg-white text-slate-500"}`} data-testid={`planner-cat-${cat.id.replace(/\s+/g, "-")}`}>
                    <Icon className="w-5 h-5" />
                    <span className="text-sm font-bold">{cat.label}</span>
                  </button>
                );
              })}
            </div>
            <button onClick={() => setSelectedCategories(CATEGORY_OPTIONS.map(c => c.id))} className="text-xs text-pink-500 font-bold hover:underline" data-testid="planner-select-all">Select All Categories</button>
            {/* Preview count */}
            {preview && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid="preview-count">
                <p className="text-xs font-bold text-emerald-700">{preview.available_offers} offers found near {city}</p>
                <p className="text-[10px] text-emerald-500">Up to ${preview.max_savings} in potential savings</p>
              </div>
            )}
            {loadingPreview && <p className="text-[10px] text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Counting offers nearby...</p>}
          </motion.div>
        )}

        {/* ═══ STEP 4: Priorities ═══ */}
        {step === 4 && (
          <motion.div key="step4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5" data-testid="planner-step-4">
            <div>
              <h2 className="text-xl font-black text-slate-800">What matters most?</h2>
              <p className="text-sm text-slate-500 mt-1">Drag to rank your priorities. Top = most important.</p>
            </div>
            <div className="space-y-2">
              {priorities.map((pId, idx) => {
                const p = PRIORITY_OPTIONS.find(x => x.id === pId);
                const Icon = p.icon;
                return (
                  <div key={pId} className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-3" data-testid={`priority-${pId}`}>
                    <span className="text-xs font-black text-slate-400 w-5">#{idx + 1}</span>
                    <Icon className={`w-5 h-5 ${p.color}`} />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-slate-700">{p.label}</p>
                      <p className="text-[10px] text-slate-400">{p.desc}</p>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => movePriority(idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-slate-100 disabled:opacity-20" data-testid={`priority-up-${pId}`}><ArrowUpDown className="w-3 h-3 text-slate-400 rotate-180" /></button>
                      <button onClick={() => movePriority(idx, 1)} disabled={idx === priorities.length - 1} className="p-1 rounded hover:bg-slate-100 disabled:opacity-20" data-testid={`priority-down-${pId}`}><ArrowUpDown className="w-3 h-3 text-slate-400" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Savings Goal */}
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-slate-700">Savings Goal</p>
                <span className="text-lg font-black text-emerald-600">${savingsGoal}</span>
              </div>
              <input type="range" min={10} max={200} step={10} value={savingsGoal} onChange={e => setSavingsGoal(parseInt(e.target.value))} className="w-full accent-emerald-500" data-testid="planner-savings-goal" />
              <div className="flex justify-between text-[9px] text-slate-400 mt-1"><span>$10</span><span>$100</span><span>$200</span></div>
            </div>
          </motion.div>
        )}

        {/* ═══ STEP 5: Review & Generate ═══ */}
        {step === 5 && (
          <motion.div key="step5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4" data-testid="planner-step-5">
            <div>
              <h2 className="text-xl font-black text-slate-800">Ready to plan!</h2>
              <p className="text-sm text-slate-500 mt-1">Review your settings and let's build your birthday adventure.</p>
            </div>
            {/* Summary cards */}
            <div className="space-y-2">
              <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid="review-location">
                <MapPin className="w-5 h-5 text-pink-500" />
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-700">{city}{state ? `, ${state}` : ""}</p>
                  <p className="text-[9px] text-slate-400">within {radiusMi} miles</p>
                </div>
                <button onClick={() => setStep(1)} className="text-[10px] text-pink-500 font-bold">Edit</button>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid="review-trip">
                <Calendar className="w-5 h-5 text-sky-500" />
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-700">{TRIP_TYPES.find(t => t.id === tripType)?.label}{tripType === "multi_day" ? ` (${numDays} days)` : ""}</p>
                  <p className="text-[9px] text-slate-400">{Object.entries(timeWindows).filter(([_, v]) => v).map(([k]) => k).join(", ")}</p>
                </div>
                <button onClick={() => setStep(2)} className="text-[10px] text-pink-500 font-bold">Edit</button>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid="review-categories">
                <Gift className="w-5 h-5 text-amber-500" />
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-700">{selectedCategories.length} categories</p>
                  <p className="text-[9px] text-slate-400 truncate">{selectedCategories.join(", ")}</p>
                </div>
                <button onClick={() => setStep(3)} className="text-[10px] text-pink-500 font-bold">Edit</button>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid="review-priorities">
                <Star className="w-5 h-5 text-amber-500" />
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-700">Top priority: {PRIORITY_OPTIONS.find(p => p.id === priorities[0])?.label}</p>
                  <p className="text-[9px] text-slate-400">Goal: ${savingsGoal}+ savings</p>
                </div>
                <button onClick={() => setStep(4)} className="text-[10px] text-pink-500 font-bold">Edit</button>
              </div>
            </div>

            {/* Prep info — auto-detected from selected categories */}
            {preview?.need_advance_signup > 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3" data-testid="planner-prep-alert">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-bold text-amber-700">
                    {preview.need_advance_signup} offer{preview.need_advance_signup !== 1 ? "s" : ""} need advance signup
                  </p>
                  <p className="text-[10px] text-amber-600 mt-0.5">
                    Start prepping up to <span className="font-bold">{preview.max_lead_days} days</span> before your birthday. We'll flag exactly which ones after your plan is generated.
                  </p>
                  {preview.need_app_download > 0 && (
                    <p className="text-[10px] text-sky-600 mt-1 flex items-center gap-1">
                      <Smartphone className="w-3 h-3" /> {preview.need_app_download} offers require an app download
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-bold text-emerald-700">No advance prep needed!</p>
                  <p className="text-[10px] text-emerald-500 mt-0.5">All offers in your selected categories can be claimed on the spot.</p>
                </div>
              </div>
            )}

            {/* Generate button */}
            <button onClick={handleGenerate} disabled={generating} className="w-full py-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-base font-black rounded-2xl hover:from-pink-600 hover:to-rose-600 disabled:opacity-50 flex items-center justify-center gap-2 shadow-xl shadow-pink-200" data-testid="planner-generate-btn">
              {generating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
              {generating ? "Building your adventure..." : "Generate My Birthday Plan!"}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex items-center gap-3 mt-6">
        {step > 1 && (
          <button onClick={() => setStep(s => s - 1)} className="flex-1 py-3 bg-slate-100 text-slate-600 text-sm font-bold rounded-xl hover:bg-slate-200" data-testid="planner-back-btn">Back</button>
        )}
        {step < totalSteps && (
          <button onClick={() => setStep(s => s + 1)} disabled={!canProceed()} className="flex-1 py-3 bg-pink-500 text-white text-sm font-bold rounded-xl hover:bg-pink-600 disabled:opacity-30 flex items-center justify-center gap-1.5" data-testid="planner-next-btn">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
